import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API, useAuth } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, IndianRupee, UserCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_INTRO = {
  director: 'Full organisation overview. Manage your team, students, teachers and platform settings.',
  admin: 'Onboard new students, manage Senior Executives, Executives and faculty.',
  senior_executive: 'Oversee your executives and the students they manage.',
  executive: 'Your assigned students at a glance. Track payments, notes and bookings.',
  finance: 'Financial view across students and collections.',
};

const StaffDashboard = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/staff/students`);
        setStudents(data);
      } catch (e) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalPaid = students.reduce((sum, s) => sum + (s.total_paid || 0), 0);
  const assignedCount = students.filter((s) =>
    user?.role === 'admin' ? s.assigned_senior_executive_id : s.assigned_executive_id
  ).length;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="border-0 shadow-lg">
      <CardContent className="p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600">{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color.replace('text', 'bg').replace('600', '100')} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="text-center py-20 text-slate-600">Loading dashboard...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="staff-dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Welcome, {user?.name}</h1>
        <p className="text-lg text-slate-600">{ROLE_INTRO[user?.role]}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          icon={Users}
          label={user?.role === 'executive' ? 'Assigned Students' : 'Students'}
          value={students.length}
          color="text-indigo-600"
        />
        {user?.role !== 'executive' && (
          <StatCard icon={UserCheck} label={user?.role === 'admin' ? 'Assigned to Sr. Executives' : 'Assigned to Executives'} value={assignedCount} color="text-violet-600" />
        )}
        <StatCard icon={IndianRupee} label="Total Collected" value={`₹${totalPaid.toFixed(0)}`} color="text-emerald-600" />
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Students</CardTitle>
          <Button asChild variant="ghost" size="sm" data-testid="staff-view-all-students">
            <Link to="/staff/students">
              View all <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No students to display yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {students.slice(0, 6).map((s) => (
                <Link
                  key={s.id}
                  to={`/staff/students/${s.id}`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 rounded-lg"
                  data-testid={`staff-recent-student-${s.id}`}
                >
                  <div>
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-sm text-slate-500">{s.email}</div>
                  </div>
                  <div className="text-sm text-slate-600">₹{(s.total_paid || 0).toFixed(0)}</div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffDashboard;
